"use client";

import { cn } from "@/app/_ui/lib/utils";
import { cancelIdleCallbackWithFallback, requestIdleCallbackWithFallback } from "@utilities/requestIdle";
import dynamic from "next/dynamic";
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { TextEditorProps, TextEditorRef } from "./types";

import { BRACKETED_TEXT_CLASSES, parseBracketedText } from "./utils/bracketedText";

// Dynamically import the actual TextEditor
const DynamicTextEditor = dynamic(() => import("./TextEditor"));

const TextEditorPlaceholder = forwardRef<HTMLDivElement, TextEditorProps>(
  ({ className, disabled = false, value = "", withBrackets = false }, ref) => {
    const renderContent = useMemo(() => {
      const displayText = value;

      if (!withBrackets) {
        return displayText;
      }

      const parts = parseBracketedText(displayText);
      return parts.map((part, index) => {
        if (!part.isBracketed) {
          return part.text;
        }

        return (
          <span className={BRACKETED_TEXT_CLASSES} data-bracketed-text='' key={index}>
            {part.text}
          </span>
        );
      });
    }, [value, withBrackets]);

    return (
      <div
        className={cn(
          "scrollbar-none relative overflow-y-scroll",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
        ref={ref}
      >
        <div className='w-full'>
          <div className={cn("!whitespace-pre-wrap focus:outline-none", className)}>{renderContent}</div>
        </div>
      </div>
    );
  },
);

TextEditorPlaceholder.displayName = "TextEditorPlaceholder";

const TextEditor = forwardRef<TextEditorRef, TextEditorProps>((props, ref) => {
  const [shouldLoadEditor, setShouldLoadEditor] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [hasPageInteraction, setHasPageInteraction] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const idleTimeoutRef = useRef<null | number>(null);

  // Start loading the editor during idle time
  useEffect(() => {
    if (shouldLoadEditor) {
      return;
    }
    const startIdleLoading = () => {
      setShouldLoadEditor(true);
    };

    idleTimeoutRef.current = requestIdleCallbackWithFallback(startIdleLoading, { timeout: 2000 });

    return () => {
      if (idleTimeoutRef.current) {
        cancelIdleCallbackWithFallback(idleTimeoutRef.current);
      }
    };
  }, [shouldLoadEditor]);

  // Handle any page interaction to show the editor
  const handlePageInteraction = useCallback(() => {
    if (!hasPageInteraction) {
      setHasPageInteraction(true);
    }
  }, [hasPageInteraction]);

  // Add global event listeners for page interaction
  useEffect(() => {
    if (hasPageInteraction) return;

    const events = ["mousedown", "mousemove", "keydown", "touchstart", "scroll"];

    const handleEvent = () => handlePageInteraction();

    events.forEach(event => {
      document.addEventListener(event, handleEvent, { passive: true, once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleEvent);
      });
    };
  }, [handlePageInteraction, hasPageInteraction]);

  // Handler for when the editor is ready
  const handleReady = useCallback(() => {
    setIsEditorReady(true);
  }, []);

  const showEditor = shouldLoadEditor && hasPageInteraction;

  return (
    <div className='relative' ref={containerRef}>
      {!isEditorReady && <TextEditorPlaceholder {...props} />}
      {showEditor && (
        <div className={cn(!isEditorReady && "absolute inset-0 opacity-0")}>
          <DynamicTextEditor {...props} onReady={handleReady} ref={ref} />
        </div>
      )}
    </div>
  );
});

TextEditor.displayName = "TextEditor";

export default memo(TextEditor);
